package ai.jessie.web.projects

import ai.jessie.shared.projects.ProjectActivityResponse
import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.shared.projects.ProjectSelectionsResponse
import ai.jessie.shared.projects.ProjectStateRequest
import ai.jessie.shared.projects.SaveProjectRequest
import ai.jessie.web.members.deniesAccess
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.OnboardingRequestFailure
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class ProjectUiState(
    val projects: List<ProjectResponse>? = null,
    val current: ProjectResponse? = null,
    val selections: ProjectSelectionsResponse? = null,
    val draft: ProjectDraft? = null,
    val pending: SaveProjectRequest? = null,
    val change: ProjectChange? = null,
    val activity: ProjectActivityResponse? = null,
    val activityUnavailable: Boolean = false,
    val removed: Boolean = false,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val notice: String? = null,
)

data class ProjectActions(
    val refresh: () -> Unit,
    val create: () -> Unit,
    val edit: () -> Unit,
    val change: ((ProjectDraft) -> ProjectDraft) -> Unit,
    val review: () -> Unit,
    val confirm: () -> Unit,
    val revise: () -> Unit,
    val cancel: () -> Unit,
    val close: () -> Unit,
    // A null position ID targets the project itself.
    val deactivate: (String?) -> Unit,
    val remove: (String?) -> Unit,
    val confirmChange: () -> Unit,
    val activity: () -> Unit,
)

/**
 * UC-010 management. Without [projectId] the state lists projects and creates one; with it the
 * state shows, edits, deactivates or removes that project and its positions. Single-flight; a
 * disposed page scope cannot publish late success, error or finalisation. Nothing is sent before
 * [confirm] or [confirmChange].
 */
class ProjectState(
    private val port: ProjectPort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val projectId: String? = null,
) {
    var state by mutableStateOf(ProjectUiState())
        private set

    val actions =
        ProjectActions(
            refresh = ::refresh,
            create = { if (projectId == null) open { ProjectDraft() } },
            edit = { open { state.current?.takeIf { projectId != null }?.draft() } },
            change = { update ->
                val draft = state.draft
                if (!state.busy && draft != null && state.pending == null)
                    state = state.copy(draft = update(draft))
            },
            review = ::review,
            confirm = ::confirm,
            revise = { if (!state.busy) state = state.copy(pending = null) },
            // A10: cancelling sends nothing.
            cancel = {
                if (!state.busy)
                    state =
                        ProjectUiState(
                            projects = state.projects,
                            current = state.current,
                            notice = "Abgebrochen. Es wurde nichts gespeichert.",
                        )
            },
            close = {
                if (!state.busy) {
                    state = ProjectUiState()
                    refresh()
                }
            },
            deactivate = { propose(ProjectOperation.DEACTIVATE, it) },
            remove = { propose(ProjectOperation.REMOVE, it) },
            confirmChange = ::confirmChange,
            activity = ::activity,
        )

    // No form, confirmation or request is open.
    private val idle
        get() = !state.busy && state.draft == null && state.change == null

    fun refresh() {
        if (state.draft != null || state.change != null) return
        request {
            if (projectId == null) {
                val projects = port.list(organisationId)
                currentCoroutineContext().ensureActive()
                state = state.copy(projects = projects)
            } else {
                val current = port.read(organisationId, projectId)
                currentCoroutineContext().ensureActive()
                state = state.copy(current = current, removed = false)
            }
        }
    }

    // Step 4: the form opens only with the customers and VAT rates the server offers right now.
    private fun open(draft: () -> ProjectDraft?) {
        if (!idle) return
        val opened = draft() ?: return
        request {
            val selections = port.selections(organisationId)
            currentCoroutineContext().ensureActive()
            state = state.copy(selections = selections, draft = opened)
        }
    }

    // Step 11: the exact request is shown for confirmation; nothing is sent yet.
    private fun review() {
        val draft = state.draft
        if (state.busy || draft == null || state.pending != null) return
        val invalid = draft.missingFields().associateWith { projectCorrection(it, "REQUIRED") }
        state =
            if (invalid.isNotEmpty())
                state.copy(problems = invalid, error = projectValidationProblem(invalid))
            else
                state.copy(
                    pending = draft.request(state.current?.takeIf { projectId != null }?.updatedAt),
                    problems = emptyMap(),
                    error = null,
                    notice = null,
                )
    }

    private fun confirm() {
        val proposal = state.pending ?: return
        request {
            val saved = port.save(organisationId, projectId, proposal.copy(confirmed = true))
            currentCoroutineContext().ensureActive()
            state =
                ProjectUiState(
                    current = saved,
                    busy = true,
                    notice = "Projekt gespeichert. Aktive Positionen stehen für Arbeit bereit.",
                )
        }
    }

    private fun propose(operation: ProjectOperation, positionId: String?) {
        if (!idle || projectId == null || state.current == null) return
        state =
            state.copy(
                change = ProjectChange(operation, projectId, positionId),
                error = null,
                notice = null,
            )
    }

    private fun confirmChange() {
        val change = state.change ?: return
        val current = state.current ?: return
        request {
            port.change(organisationId, change, ProjectStateRequest(current.updatedAt, true))
            currentCoroutineContext().ensureActive()
            val removed = change.operation == ProjectOperation.REMOVE
            // The stored change is reported before the reload, whose failure cannot undo it.
            state = ProjectUiState(busy = true, notice = changedNotice(change), removed = removed)
            if (!removed || change.positionId != null) {
                val reloaded = port.read(organisationId, change.projectId)
                currentCoroutineContext().ensureActive()
                state = state.copy(current = reloaded, removed = false)
            }
        }
    }

    // A2: shown only from the server's answer; an unavailable integration never becomes zero.
    private fun activity() {
        if (projectId == null || state.draft != null || state.change != null) return
        request {
            val activity = port.activity(organisationId, projectId)
            currentCoroutineContext().ensureActive()
            state = state.copy(activity = activity, activityUnavailable = false)
        }
    }

    private fun request(block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: ProjectRequestFailure) {
                currentCoroutineContext().ensureActive()
                rejected(failure)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered write leaves the stored state unknown; reads changed nothing.
                val written = state.pending != null || state.change != null
                val error = if (written) uncertainProjectProblem() else unavailableProblem()
                state = state.copy(pending = null, change = null, error = error)
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }

    private fun rejected(failure: ProjectRequestFailure) {
        val detail = failure.detail
        val error = failure.projectProblem()
        state =
            when {
                // A12, A13 and a stale record: nothing previously loaded stays on the page.
                OnboardingRequestFailure(failure.status, detail.code).deniesAccess() ||
                    detail.code == "STALE_PROJECT" -> ProjectUiState(busy = true, error = error)
                detail.code == "INVALID_PROJECT" -> {
                    val problems =
                        detail.problems.associate {
                            it.field to projectCorrection(it.field, it.reason)
                        }
                    state.copy(
                        pending = null,
                        change = null,
                        problems = problems,
                        error = projectValidationProblem(problems),
                    )
                }
                // A8: the record stays; deactivation is offered when it is still active.
                detail.code == "REFERENCED_RECORD" -> {
                    val positionId = state.change?.positionId
                    state.copy(change = null, error = referencedProblem(activeTarget(positionId)))
                }
                detail.code == "ACTIVITY_UNAVAILABLE" ->
                    state.copy(activity = null, activityUnavailable = true)
                // A3: back to the form with the customer marked.
                detail.code.startsWith("CUSTOMER_CHANGE_") ->
                    state.copy(pending = null, error = error)
                // A11: the review stays open so that the member can confirm again.
                failure.status >= HTTP_SERVER_ERROR -> state.copy(error = error)
                else -> state.copy(pending = null, change = null, error = error)
            }
    }

    private fun activeTarget(positionId: String?): String? {
        val current = state.current ?: return null
        val active =
            if (positionId == null) current.active
            else current.positions.any { it.details.id == positionId && it.details.active }
        return deactivateId(positionId).takeIf { active }
    }
}

private fun changedNotice(change: ProjectChange): String {
    val target = if (change.positionId == null) "Projekt" else "Position"
    return when (change.operation) {
        ProjectOperation.DEACTIVATE ->
            "$target deaktiviert. Bestehende Verknüpfungen bleiben erhalten."
        ProjectOperation.REMOVE -> "$target endgültig entfernt."
    }
}
